import React from 'react';
import DocLayout from '../DocLayout';
import { Users, UserPlus, Mail } from 'lucide-react';

const InviteTeam = () => {
  return (
    <DocLayout title="Inviting Your Team" category="Getting Started">
      <div className="space-y-6">
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-indigo-600" />
            Team Collaboration
          </h2>
          <p className="text-slate-700 mb-4">
            I-Match is designed for team collaboration. Add your team members to share leads, tasks, and insights.
          </p>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Adding Team Members</h3>
          <p className="text-slate-700 mb-3">
            As an admin user, you can invite team members:
          </p>
          <ol className="list-decimal pl-6 space-y-3 text-slate-700">
            <li>
              <strong>Navigate to Admin Panel</strong>
              <p className="text-sm mt-1">Click on "Admin Panel" in the main navigation menu</p>
            </li>
            <li>
              <strong>Go to Users Tab</strong>
              <p className="text-sm mt-1">Select the "Users" tab to see all team members</p>
            </li>
            <li>
              <strong>Click "Create User"</strong>
              <p className="text-sm mt-1">Click the "+" or "Create User" button</p>
            </li>
            <li>
              <strong>Fill in User Details</strong>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-sm">
                <li>Email address (required)</li>
                <li>First name and last name</li>
                <li>Password (minimum 8 characters)</li>
                <li>Role (Admin or User)</li>
                <li>Export permission (optional)</li>
              </ul>
            </li>
            <li>
              <strong>Submit</strong>
              <p className="text-sm mt-1">The user account will be created immediately</p>
            </li>
          </ol>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Understanding Roles</h3>
          <div className="space-y-3">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <UserPlus className="w-5 h-5 text-indigo-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Admin Role</h4>
                  <p className="text-slate-700 text-sm mb-2">Grant admin role to team leads and managers who need:</p>
                  <ul className="list-disc pl-6 space-y-1 text-slate-700 text-sm">
                    <li>User management capabilities</li>
                    <li>Company profile editing</li>
                    <li>Access to usage statistics</li>
                    <li>Admin panel features</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-blue-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">User Role</h4>
                  <p className="text-slate-700 text-sm mb-2">Assign user role to sales reps and team members who:</p>
                  <ul className="list-disc pl-6 space-y-1 text-slate-700 text-sm">
                    <li>Manage leads and contacts</li>
                    <li>Create tasks and events</li>
                    <li>Use AI enrichment features</li>
                    <li>Send emails using templates</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Export Permissions</h3>
          <p className="text-slate-700 mb-3">
            Control who can export lead data:
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-slate-700 text-sm">
              <strong>Export Permission</strong> allows users to export leads data to CSV format. Enable this only for trusted team members who need data analysis capabilities.
            </p>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">User Limits by Plan</h3>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Plan</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Max Users</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Price per Extra User</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="px-4 py-3 text-sm">Base</td>
                  <td className="px-4 py-3 text-sm">5 users</td>
                  <td className="px-4 py-3 text-sm">€4/user/month</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm">Tier 1</td>
                  <td className="px-4 py-3 text-sm">25 users</td>
                  <td className="px-4 py-3 text-sm">€4/user/month</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm">Tier 2</td>
                  <td className="px-4 py-3 text-sm">50 users</td>
                  <td className="px-4 py-3 text-sm">€4/user/month</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Managing Existing Users</h3>
          <p className="text-slate-700 mb-3">
            From the Users tab in Admin Panel, you can:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li><strong>Edit User</strong>: Update name, role, or permissions</li>
            <li><strong>Deactivate User</strong>: Temporarily disable access</li>
            <li><strong>Delete User</strong>: Permanently remove user account</li>
            <li><strong>Reset Password</strong>: Set a new password for users</li>
            <li><strong>Impersonate User</strong>: View the system as that user (admin only)</li>
          </ul>
        </section>
      </div>
    </DocLayout>
  );
};

export default InviteTeam;